/**
 * QuickOpen - VS Code style file search overlay (Cmd+P / Ctrl+P)
 * 
 * Appears as a floating command palette at the top center of the editor
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Search, File, X, Loader2 } from 'lucide-react';
import { useKeyboardShortcuts, createCrossplatformShortcut } from '@/hooks/useKeyboardShortcuts';
import { cn } from '@/lib/utils';
import type { FileNode } from '@/components/molecules/metadata_v1';
import { useSearchMetadataItems } from '@/hooks/metadata_v1';
import { useDebounce } from '@/utils/useDebounce';

interface QuickOpenProps {
  files: FileNode[];
  onFileSelect: (file: FileNode) => void;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** When provided, only show results whose metadataType is in this list. */
  allowedMetadataTypes?: readonly string[];
}

export function QuickOpen({ files, onFileSelect, isOpen: controlledIsOpen, onOpenChange, allowedMetadataTypes }: QuickOpenProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  
  // Support both controlled and uncontrolled modes
  const isOpen = controlledIsOpen ?? internalIsOpen;
  const setIsOpen = (value: boolean) => {
    setInternalIsOpen(value);
    onOpenChange?.(value);
  };

  // Debounce search query to reduce API calls
  const debouncedQuery = useDebounce(query, 300);

  // Search API - only enabled when query is at least 2 characters
  const { data: searchResults = [], isLoading: isSearching } = useSearchMetadataItems(
    debouncedQuery,
    {
      enabled: isOpen && debouncedQuery.trim().length >= 2,
    }
  );


  const allowedSet = useMemo(
    () => (allowedMetadataTypes ? new Set(allowedMetadataTypes) : null),
    [allowedMetadataTypes]
  );

  // Convert search results to FileNode format
  const searchResultFiles = useMemo(() => {
    const mapped = searchResults.map(item => ({
      id: item.id,
      name: item.fileName,
      path: item.fullName,
      type: 'file' as const,
      metadataType: item.metadataType,
    }));
    if (!allowedSet) return mapped;
    return mapped.filter(f => f.metadataType && allowedSet.has(f.metadataType));
  }, [searchResults, allowedSet]);

  // Flatten file tree to searchable list (fallback for empty query)
  const flattenedFiles = useMemo(() => {
    const result: FileNode[] = [];
    
    function flatten(nodes: FileNode[], path: string = '') {
      for (const node of nodes) {
        const fullPath = path ? `${path}/${node.name}` : node.name;
        if (node.type === 'file') {
          result.push({ ...node, path: fullPath });
        }
        if (node.children) {
          flatten(node.children, fullPath);
        }
      }
    }
    
    flatten(files);
    if (!allowedSet) return result;
    return result.filter(f => f.metadataType && allowedSet.has(f.metadataType));
  }, [files, allowedSet]);

  // Determine which files to display
  const filteredFiles = useMemo(() => {
    // If query is less than 2 characters, show recently loaded files
    if (query.trim().length < 2) {
      return flattenedFiles.slice(0, 20);
    }
    
    // If searching via API, use search results
    return searchResultFiles;
  }, [query, flattenedFiles, searchResultFiles]);

  // Open handler
  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setQuery('');
    setSelectedIndex(0);
    // Focus input after render
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  // Close handler
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  // Select file handler
  const handleSelect = useCallback((file: FileNode) => {
    onFileSelect(file);
    handleClose();
  }, [onFileSelect, handleClose]);

  // Register Cmd+P / Ctrl+P shortcut
  useKeyboardShortcuts({
    shortcuts: [
      createCrossplatformShortcut('p', handleOpen, { description: 'Quick Open' }),
    ],
  });

  // Handle keyboard navigation in the list
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredFiles.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredFiles[selectedIndex]) {
          handleSelect(filteredFiles[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        handleClose();
        break;
    }
  }, [filteredFiles, selectedIndex, handleSelect, handleClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && filteredFiles.length > 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      selectedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, filteredFiles.length]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-quick-open]')) {
        handleClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  // Global Escape key handler (works even without input focus)
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />
      
      {/* Quick Open Panel */}
      <div 
        data-quick-open
        className="relative mx-4 w-full max-w-xl bg-popover border border-border rounded-lg shadow-2xl overflow-hidden"
      >
        {/* Search Input */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          {isSearching ? (
            <Loader2 className="h-4 w-4 text-muted-foreground shrink-0 animate-spin" />
          ) : (
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search files by name..."
            className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground"
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-0.5 hover:bg-muted rounded"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>
        
        {/* Results List */}
        <div 
          ref={listRef}
          className="max-h-80 overflow-auto py-1"
        >
          {filteredFiles.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No files found
            </div>
          ) : (
            filteredFiles.map((file, index) => (
              <QuickOpenItem
                key={file.id}
                file={file}
                isSelected={index === selectedIndex}
                query={query}
                onClick={() => handleSelect(file)}
                onMouseEnter={() => setSelectedIndex(index)}
              />
            ))
          )}
        </div>
        
        {/* Footer hint */}
        <div className="px-3 py-1.5 border-t border-border bg-muted/30 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>
            <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">↑↓</kbd> navigate
            <span className="mx-2">•</span>
            <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">↵</kbd> open
            <span className="mx-2">•</span>
            <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">esc</kbd> close
          </span>
          <span>{filteredFiles.length} files</span>
        </div>
      </div>
    </div>
  );
}

interface QuickOpenItemProps {
  file: FileNode;
  isSelected: boolean;
  query: string;
  onClick: () => void;
  onMouseEnter: () => void;
}

function QuickOpenItem({ file, isSelected, query, onClick, onMouseEnter }: QuickOpenItemProps) {
  // Highlight matching text
  const highlightMatch = (text: string) => {
    if (!query.trim()) return text;
    
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);
    
    if (index === -1) return text;
    
    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-500/30 text-yellow-600 dark:text-yellow-400">
          {text.slice(index, index + query.length)}
        </span>
        {text.slice(index + query.length)}
      </>
    );
  };

  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={cn(
        'w-full px-3 py-1.5 flex items-center gap-2 text-left transition-colors',
        isSelected 
          ? 'bg-accent text-accent-foreground' 
          : 'hover:bg-muted/50'
      )}
    >
      <File className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">
          {highlightMatch(file.name)}
        </div>
        {file.path && file.path !== file.name && (
          <div className="text-xs text-muted-foreground truncate">
            {file.path}
          </div>
        )}
      </div>
      {file.metadataType && (
        <span className="text-[10px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground shrink-0">
          {file.metadataType}
        </span>
      )}
    </button>
  );
}
